import { AudioEngineRemoteAudio } from '../../RoomContext'
import { AudioTrackWithInfo, AudioInputStreamInfo } from '../../UserContext'
import { AudioEngineError } from './index'
import { AudioEngineJoinRoomParams, IAudioEngineRoom } from './IAudioEngineRoom'
import { WebAudioEngineRoom } from './WebAudioEngineRoom'
import { AudioEngineRoom } from './AudioEngineRoom'
import { logger } from '../../services/Logger'
import { AudioPatch } from './types'

const log = logger.createChildLogger({ name: 'HybridAudioEngineRoom' })

export class HybridAudioEngineRoom implements IAudioEngineRoom {
    private audioEngineRoom: AudioEngineRoom
    private webAudioEngineRoom: WebAudioEngineRoom

    constructor(audioEngineRoom: AudioEngineRoom, webAudioEngineRoom: WebAudioEngineRoom) {
        this.audioEngineRoom = audioEngineRoom
        this.webAudioEngineRoom = webAudioEngineRoom
    }

    async joinRoom(data: AudioEngineJoinRoomParams): Promise<string | AudioEngineError> {
        // Let the web audio engine connect first and see which relay it
        // resolves to. Then connect native audio engine to the same relay.
        const webResult = await this.webAudioEngineRoom.joinRoom(data)
        if (webResult instanceof AudioEngineError) {
            return webResult
        } else {
            const connectedWsUrl = webResult.endsWith('/') ? webResult.slice(0, -1) : webResult
            let params = data
            const wsNode = data.servers.find((s) => s.websocketUrl === connectedWsUrl)
            if (wsNode) {
                params = { ...data, servers: [wsNode] }
            } else {
                log.error(
                    'joinRoom',
                    `The web audio engine connected to ${webResult}, but no matching relay node could be found. Native audio engine will try all nodes.`
                )
            }

            const nativeResult = await this.audioEngineRoom.joinRoom(params)
            if (nativeResult instanceof AudioEngineError) {
                return nativeResult
            }
        }

        return webResult
    }

    async exitRoom(): Promise<boolean> {
        const r1 = await this.audioEngineRoom.exitRoom()
        const r2 = await this.webAudioEngineRoom.exitRoom()

        return r1 && r2
    }

    sendLocalStream(track: AudioTrackWithInfo): Promise<boolean> {
        return this.audioEngineRoom.sendLocalStream(track)
    }

    receiveRemoteStream(
        streamUuid: string,
        info: AudioInputStreamInfo
    ): Promise<AudioEngineError | AudioEngineRemoteAudio> {
        return this.webAudioEngineRoom.receiveRemoteStream(streamUuid, info)
    }

    removeRemoteStream(streamUuid: string): Promise<boolean> {
        return this.webAudioEngineRoom.removeRemoteStream(streamUuid)
    }

    createAudioPatch(sourceStreamId: string, targetDeviceId: string, targetChannel: number): void {
        this.webAudioEngineRoom.createAudioPatch(sourceStreamId, targetDeviceId, targetChannel)
    }

    removeAudioPatch(sourceStreamId: string): void {
        this.webAudioEngineRoom.removeAudioPatch(sourceStreamId)
    }

    removeAllAudioPatches(): void {
        this.webAudioEngineRoom.removeAllAudioPatches()
    }

    listAudioPatches(): AudioPatch[] {
        return this.webAudioEngineRoom.listAudioPatches()
    }

    destroy(): void {
        this.audioEngineRoom.destroy()
        this.webAudioEngineRoom.destroy()
    }
}
